import axios from 'axios';
import authHeader from './auth-header';
import { apiUrl } from '../constant';
import { resolveRoleId } from '../constants/roleConstants';

const API_URL = apiUrl;

class DashboardAnalyticsService {
    getRoleIdFromUser(user, viewAsRole) {
        return resolveRoleId(user, viewAsRole);
    }

    // Normalize mixed date inputs and return an unambiguous date-only format.
    // This avoids MM-DD vs DD-MM confusion in dashboard filters.
    formatDateForApi(dateInput) {
        if (!dateInput) {
            return null;
        }

        let parsedDate = null;

        if (dateInput instanceof Date) {
            parsedDate = dateInput;
        } else if (typeof dateInput === 'string') {
            const isoDateOnlyMatch = /^\d{4}-\d{2}-\d{2}$/.test(dateInput);
            const mmddyyyyMatch = /^\d{2}-\d{2}-\d{4}$/.test(dateInput);

            if (isoDateOnlyMatch) {
                const [year, month, day] = dateInput.split('-').map(Number);
                parsedDate = new Date(year, month - 1, day);
            } else if (mmddyyyyMatch) {
                const [month, day, year] = dateInput.split('-').map(Number);
                parsedDate = new Date(year, month - 1, day);
            } else {
                parsedDate = new Date(dateInput);
            }
        } else {
            parsedDate = new Date(dateInput);
        }

        if (!(parsedDate instanceof Date) || Number.isNaN(parsedDate.getTime())) {
            return null;
        }

        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Map frontend filters to backend DashboardFilterModel format
    mapFiltersToBackend(filters) {
        const userDetails = JSON.parse(localStorage.getItem('userDetails'));
        const user = userDetails?.Value;
        const selectedSellerId =
            filters?.companySellerId ??
            filters?.sellerId ??
            user?.companySellerId ??
            user?.CompanySellerId ??
            null;
        const tenantId = user?.tenantId || user?.TenantId || 0;
        // Try multiple field names for employeeId - prioritize filters first, then user object
        const employeeId =
            filters?.employeeId ||
            user?.employeeId ||
            user?.EmployeeId ||
            user?.id ||
            user?.Id ||
            user?.userId ||
            user?.UserId ||
            0;
        // Send explicit role only when user selected "View As".
        // Otherwise send 0 so backend uses JWT role as source of truth.
        const roleId =
            filters?.viewAsRole !== null &&
            filters?.viewAsRole !== undefined &&
            filters?.viewAsRole !== ''
                ? Number(filters.viewAsRole) || 0
                : 0;

        const fromDateFormatted = this.formatDateForApi(filters.fromDate) ||
            this.formatDateForApi(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

        const toDateFormatted = this.formatDateForApi(filters.toDate) ||
            this.formatDateForApi(new Date());

        // Keys must match [JsonProperty] camelCase names on DashboardFilterModel / BaseModel
        return {
            startDate: fromDateFormatted,
            endDate: toDateFormatted,
            // Send selected seller only. If none selected, keep 0 and let backend/user role decide scope.
            companySellerId: Number(selectedSellerId) || 0,
            sellerId: Number(selectedSellerId) || 0,
            tenantId: Number(tenantId) || 0,
            role: roleId,
            employeeId: Number(employeeId) || 0,
            commaSeparatedIds: filters.commaSeparatedIds || '',
            listOrderNumber: filters.listOrderNumber || [],
            isPrinted: filters.isPrinted || false,
            needsSeparation: filters.needsSeparation || false,
            includePageIndex: false
        };
    }

    getKPISummary(filters) {
        return axios.post(
            `${API_URL}/api/DashboardAnalytics/GetKPISummary`,
            this.mapFiltersToBackend(filters),
            { headers: authHeader() }
        );
    }

    getLabelsByStatus(filters) {
        return axios.post(
            `${API_URL}/api/DashboardAnalytics/GetLabelsByStatus`,
            this.mapFiltersToBackend(filters),
            { headers: authHeader() }
        );
    }

    getOrderVolumeChart(filters) {
        return axios.post(
            `${API_URL}/api/DashboardAnalytics/GetOrderVolumeChart`,
            this.mapFiltersToBackend(filters),
            { headers: authHeader() }
        );
    }

    getLabelsByCarrier(filters) {
        return axios.post(
            `${API_URL}/api/DashboardAnalytics/GetLabelsByCarrier`,
            this.mapFiltersToBackend(filters),
            { headers: authHeader() }
        );
    }

    getInboundByStatus(filters) {
        return axios.post(
            `${API_URL}/api/DashboardAnalytics/GetInboundByStatus`,
            this.mapFiltersToBackend(filters),
            { headers: authHeader() }
        );
    }

    getTasksByType(filters) {
        return axios.post(
            `${API_URL}/api/DashboardAnalytics/GetTasksByType`,
            this.mapFiltersToBackend(filters),
            { headers: authHeader() }
        );
    }

    // ============================================================================
    // NEW AGGREGATION ENDPOINT - Replaces all individual calls above
    // ============================================================================
    
    /**
     * Fetch all 41 dashboard widgets in a single API call with parallel execution
     * Returns aggregated response with KPIs, charts, and metadata
     * @param {Object} filters - Dashboard filter parameters
     * @returns {Promise} Axios response with aggregated dashboard data
     */
    getAggregatedDashboard(filters) {
        return axios.post(
            `${API_URL}/api/DashboardAggregation/GetAllWidgets`,
            this.mapFiltersToBackend(filters),
            { headers: authHeader() }
        );
    }

    /**
     * Fetch all companies for dropdown filter
     * Auto-filtered by TenantId from JWT token
     * @returns {Promise} Axios response with company list
     */
    getCompanies() {
        return axios.post(
            `${API_URL}/GetAllCompany`,
            {
                pageIndex: 1,
                pageSize: 1000,
                sortColumn: 'id',
                sortOrder: 'DESCENDING',
                screenType: 1
            },
            { headers: authHeader() }
        );
    }

    /**
     * Fetch sellers for selected company IDs
     * Auto-filtered by TenantId from JWT token
     * @param {Array} companyIds - Array of company IDs to filter by
     * @returns {Promise} Axios response with seller list
     */
    getSellersByCompanyIds(companyIds) {
        return axios.post(
            `${API_URL}/api/GetSellersByCompanyIds`,
            {
                companyIds: companyIds || [],
                pageIndex: 1,
                pageSize: 1000,
                sortColumn: 'id',
                sortOrder: 'DESCENDING',
                screenType: 1
            },
            { headers: authHeader() }
        );
    }
}

export default new DashboardAnalyticsService();
